import { defineStore } from 'pinia'
import { useAuthStore } from '@/stores/auth/store'
import { useOfficeDeskStore, type OfficeDesk } from '@/stores/officeDesk/store'
import * as Api from './api'
import { callWithMeta, createMeta, waitFor, type Meta } from '@/utils/store'
import * as ApiOfficeDesk from '@/stores/officeDesk/api'
import { useExtensionsStore } from '../extensions/store'
import { useExtensionStore } from '../extension/store'
import { useDevicesStore } from '../devices/store'
import { useDeviceStore } from '../device/store'
import type { ColorName } from '@/utils/colors'

type State = {
  officeDesks: OfficeDesk[]
  meta: Meta<{
    officeDesks: undefined
    load: undefined
    changeColor: undefined
    update: undefined
    delete: undefined
  }>
}

const getDefaultState = (): State => ({
  officeDesks: [],
  meta: createMeta(['officeDesks', 'load', 'changeColor', 'update', 'delete']),
})

export const useOfficeDesksStore = defineStore('officeDesks', {
  state: getDefaultState,

  getters: {
    find: (state) => (id: string | number) =>
      state.officeDesks.find((officeDesk) => officeDesk.id === Number(id)),
    list: (state) => state.officeDesks,
  },

  actions: {
    RESET() {
      this.officeDesks = getDefaultState().officeDesks
      this.meta.officeDesks = getDefaultState().meta.officeDesks
    },

    async load({ useCache = true } = {}) {
      const hasCached = ['pending', 'success'].includes(
        this.meta.officeDesks.status
      )

      if (useCache && hasCached) {
        await waitFor(() => this.meta.officeDesks.status !== 'pending')

        return
      }

      const { result } = await callWithMeta({
        store: this,
        metaFields: { officeDesks: undefined, load: undefined },
        apiCall: async () => {
          const isOfficeDesk = useAuthStore().isOfficeDesk

          if (isOfficeDesk) {
            const officeDeskId = useAuthStore().officeDeskId!

            const result = await Api.loadOfficeDesk(officeDeskId)

            if (result.status === 'success') {
              return {
                status: 'success',
                payload: [result.payload],
              }
            }

            return result
          }

          return await Api.loadOfficeDesks()
        },
        onSuccess: ({ result, isLatest }) => {
          if (isLatest('officeDesks')) {
            this.officeDesks = result.payload
          }
        },
      })

      if (result.status === 'error') {
        throw result.payload
      }
    },

    async changeColor({
      color,
      itemId,
    }: {
      color: ColorName | null
      itemId: number
    }) {
      // Show color change before waiting for API call

      const officeDeskIndex = this.officeDesks.findIndex(
        (officeDesk) => officeDesk.id === itemId
      )

      if (officeDeskIndex >= 0) {
        this.officeDesks[officeDeskIndex].color = color
      }

      // Send color change to the BE

      const { result } = await callWithMeta({
        store: this,
        metaFields: { changeColor: undefined },
        apiCall: async () =>
          await ApiOfficeDesk.updateOfficeDesk(itemId, { color }),
        onSuccess: ({ result, isLatest }) => {
          if (isLatest('changeColor')) {
            const updatedOfficeDesk = result.payload

            const officeDeskIndex = this.officeDesks.findIndex(
              (officeDesk) => officeDesk.id === updatedOfficeDesk.id
            )

            this.officeDesks[officeDeskIndex] = updatedOfficeDesk
          }
        },
      })

      if (result.status === 'error') {
        throw result.payload
      }
    },

    async update(data: {
      userIds: number[]
      preferences: { name: string; value: any }[]
    }) {
      const { result } = await callWithMeta({
        store: this,
        metaFields: { officeDesks: undefined, update: undefined },
        apiCall: async () => await Api.updateOfficeDesks(data),
        onSuccess: ({ isLatest }) => {
          if (isLatest('update')) {
            setTimeout(async () => {
              await this.load({ useCache: false })

              useOfficeDeskStore().reload()
            })
          }
        },
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },

    async delete(userIds: number[]) {
      const { result } = await callWithMeta({
        store: this,
        metaFields: { officeDesks: undefined, delete: undefined },
        apiCall: async () => await Api.deleteOfficeDesks(userIds),
        onSuccess: () => {
          this.officeDesks = this.officeDesks.filter(
            (officeDesk) => !userIds.includes(officeDesk.id)
          )

          if (
            userIds.includes(Number(useOfficeDeskStore().officeDeskData?.id))
          ) {
            useOfficeDeskStore().RESET()
          }

          useExtensionsStore().load({ useCache: false })

          useExtensionStore().reload()

          useDevicesStore().load({ useCache: false })

          useDeviceStore().reload()
        },
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },
  },
})
